// apps/web/src/features/users/components/UserCreateModal.tsx
// inline fields + useCreateUser. On success: toast + close. On error: toast
// the localized message via errorMessage().
//
// Issue #628 PR3 (now #680): all close paths (Modal chrome X, Escape,
// backdrop, Cancel) route through the EditorModal wrapper guard so
// typed-but-unsaved field values cannot disappear silently. Dirty is
// computed locally by comparing the current watched values against
// `DEFAULTS` for the user-editable fields only. We CANNOT use RHF
// `formState.isDirty` here because the embedded `usePhoneInput` library
// fires a transient `onChange` during mount/blur (dial-code normalization)
// that flips `isDirty` to true even for an untouched form. SECURITY: only
// the resulting boolean (no field values, no password) is passed to the
// wrapper. Successful creation calls `reset(DEFAULTS)` + `setDirty(false)`
// so the post-success `onClose()` does not trip a stale warning.
import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CreateUserInputSchema, type CreateUserInput, type RoleId } from '@magimp/shared';
import { useCreateUser, useDepartmentsList, useRolesList } from '@magimp/shared-hooks';
import {
  EditorModal,
  Button,
  Input,
  PhoneInput,
  Select,
  FormRow,
  useEditorGuard,
  type SelectOption,
} from '../../../ui/index.js';
import { PasswordField } from '../../../account/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { phoneError } from '../../../shared/index.js';

export interface UserCreateModalProps {
  onClose: () => void;
}

// `CreateUserInputSchema` is `UserSchema.omit({ id, createdAt,
// passwordResetRequired, role }).extend({ roleId, password })` — so `active`
// IS part of the wire input. The form starts with `roleId: ''` which fails
// schema validation until the user picks a role from /api/roles.
// WHY: empty string is not a valid `RoleId` brand at the type level, but RHF
// holds form state in the input shape; submit-time `zodResolver` rejects
// unset values and shows an error. We only cross the brand boundary in
// `defaultValues`, never on submit.
const DEFAULTS: CreateUserInput = {
  email: '',
  name: '',
  roleId: '' as unknown as RoleId,
  phone: '',
  departmentId: '',
  password: '',
  active: true,
};

export function UserCreateModal({ onClose }: UserCreateModalProps) {
  const { t } = useTranslation(['users', 'common']);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(CreateUserInputSchema),
    defaultValues: DEFAULTS,
  });
  const create = useCreateUser();
  // Local dirty derivation. Compares current values against `DEFAULTS`
  // for the user-typed fields only. For phone we tolerate the
  // `usePhoneInput` lib's transient dial-only emissions (e.g. '+7') by
  // treating any non-empty value of length <=4 starting with '+' as
  // still-clean; real user input always carries more digits than the
  // dial code itself.
  const [dirty, setDirty] = useState(false);
  const computeDirty = useCallback((values: Partial<CreateUserInput>): boolean => {
    if ((values.email ?? '') !== DEFAULTS.email) return true;
    if ((values.name ?? '') !== DEFAULTS.name) return true;
    if ((values.departmentId ?? '') !== DEFAULTS.departmentId) return true;
    if ((values.roleId ?? '') !== (DEFAULTS.roleId as unknown as string)) return true;
    if ((values.password ?? '') !== DEFAULTS.password) return true;
    // Phone: empty baseline. The phone lib may briefly emit a dial-only
    // value during mount/blur. Treat '' OR a short '+<dialCode>' (length
    // <=4) as clean. Real user input starts with '+' and has more digits
    // than the dial code, so length > 4 reliably means "typed".
    const phone = values.phone ?? '';
    if (phone !== '' && !(phone.startsWith('+') && phone.length <= 4)) return true;
    return false;
  }, []);
  useEffect(() => {
    setDirty(computeDirty(watch()));
    const sub = watch((values) => {
      setDirty(computeDirty(values));
    });
    return () => sub.unsubscribe();
  }, [watch, computeDirty]);
  const departmentsQ = useDepartmentsList({ active: true });
  const departmentOptions: SelectOption[] = (departmentsQ.data ?? [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    .map((d) => ({ value: d.id, label: d.name }));
  const rolesQ = useRolesList();
  const roleOptions: SelectOption[] = (rolesQ.data ?? []).map((r) => ({
    value: r.id,
    label: r.displayName,
  }));
  const show = useToastStore((s) => s.show);

  const onSubmit = (values: CreateUserInput) => {
    create.mutate(values, {
      onSuccess: () => {
        show({ tone: 'success', text: t('users:toasts.created') });
        // Re-baseline back to empty defaults; flip dirty=false immediately
        // so the subsequent `onClose()` does not trip a stale warning even
        // before the next watch tick fires.
        reset(DEFAULTS);
        setDirty(false);
        onClose();
      },
      onError: (err) => {
        show({ tone: 'error', text: errorMessage(err) });
      },
    });
  };

  return (
    <EditorModal
      open={true}
      title={t('users:modals.createTitle')}
      width={480}
      dirty={dirty}
      busy={create.isPending}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRow label="Email" error={errors.email?.message}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.email}
                type="email"
                icon="mail"
                autoComplete="off"
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.name')} error={errors.name?.message}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input value={field.value ?? ''} onChange={field.onChange} invalid={!!errors.name} />
            )}
          />
        </FormRow>
        <FormRow
          label={t('users:fields.phone')}
          error={phoneError(errors.phone?.message, t('users:errors.invalidPhone'))}
        >
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.phone}
                testId="user-create-phone"
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.department')} error={errors.departmentId?.message}>
          <Controller
            control={control}
            name="departmentId"
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v)}
                options={departmentOptions}
                placeholder={
                  departmentsQ.isPending
                    ? t('users:placeholders.departmentLoading')
                    : t('users:placeholders.departmentSelect')
                }
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.role')} error={errors.roleId?.message}>
          <Controller
            control={control}
            name="roleId"
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v)}
                options={roleOptions}
                placeholder={
                  rolesQ.isPending
                    ? t('users:placeholders.roleLoading')
                    : t('users:placeholders.roleSelect')
                }
              />
            )}
          />
        </FormRow>
        <FormRow label={t('users:fields.password')} error={errors.password?.message}>
          <Controller
            control={control}
            name="password"
            render={({ field }) => (
              <PasswordField
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.password}
                hint={t('users:password.requirement')}
                showGenerate
                showCopy
                autoComplete="new-password"
              />
            )}
          />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
          <Button type="submit" kind="primary" disabled={create.isPending}>
            {t('common:actions.create')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

// WHY: Cancel is an in-form close affordance, not a Modal-chrome close path
// (X / Esc / backdrop) — those are handled by EditorModal internally. Route
// Cancel through `confirmDestructive` so a dirty form still prompts before
// discarding typed input.
function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
